import { memo, useRef, useState } from 'react'

import { Nullable } from '@types'

import { CustomTooltip } from '@components/CustomTooltip'
import TruncateLines from '@components/TruncateLines'
import ContactsOverviewCard from '@modules/contacts/components/ContactsOverviewCard'
import { MEETING_STATUS_ORDER, MEETING_STATUS_TO_COLOR_MAP } from '@modules/contacts/constants'
import { CompanyContactOverview, MeetingStatus } from '@modules/contacts/types'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'

import {
  ArrowIconButton,
  ContactCountsCellWrapper,
  StatusCountContainer,
  StatusCountDot,
  StatusCountsContainer,
} from './ContactCountsCell.styles'

interface ContactCountsCellProps {
  companyId: number
  contactsOverview: CompanyContactOverview
  onExpandClick: (ref: Nullable<HTMLElement>, companyId?: number) => void
}

const ContactCountsCell = ({ contactsOverview, companyId, onExpandClick }: ContactCountsCellProps) => {
  const { counts, recentMeeting } = contactsOverview

  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const containerRef = useRef(null)

  const handleDetailsClick = () => {
    setIsTooltipOpen(false)
    onExpandClick(containerRef.current)
  }

  return (
    <CustomTooltip
      onClose={() => setIsTooltipOpen(false)}
      onOpen={() => setIsTooltipOpen(true)}
      open={isTooltipOpen}
      color="transparent"
      arrow={false}
      padding="0px"
      title={
        <ContactsOverviewCard companyId={companyId} onDetailsClick={handleDetailsClick} counts={counts} meeting={recentMeeting} />
      }
    >
      <ContactCountsCellWrapper ref={containerRef}>
        <Box display="flex" alignItems="center" onClick={handleDetailsClick} className="history-button">
          <ArrowIconButton color="primary" />
        </Box>

        <Box sx={{ width: '100%' }}>
          {recentMeeting?.status === MeetingStatus.UPCOMING && (
            <StatusCountContainer>
              <StatusCountDot color={MEETING_STATUS_TO_COLOR_MAP[MeetingStatus.UPCOMING]?.bar} />
              <TruncateLines lines={4}>
                <Typography variant="caption">{recentMeeting.event.name}</Typography>
              </TruncateLines>
            </StatusCountContainer>
          )}

          <StatusCountsContainer>
            {!!counts &&
              MEETING_STATUS_ORDER.map(status =>
                counts[status] ? (
                  <StatusCountContainer key={status}>
                    <StatusCountDot color={MEETING_STATUS_TO_COLOR_MAP[status]?.bar} />
                    <Typography variant="body1">{counts[status]}</Typography>
                  </StatusCountContainer>
                ) : null,
              )}
          </StatusCountsContainer>
        </Box>
      </ContactCountsCellWrapper>
    </CustomTooltip>
  )
}

export default memo(ContactCountsCell)
